import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

interface Subprocessor {
  name: string;
  role: string;
  region: string;
  dataReceived: string;
  safeguard: string;
  homepage: string;
  privacyUrl?: string;
}

/**
 * Production subprocessors. The audit report at
 * docs/audits/popia-2026-04-29.md established this list as the baseline;
 * the next quarterly audit diffs against package.json + pyproject.toml +
 * this list to detect drift.
 *
 * Order: data-store services first, then channel-of-record services
 * (email), then analytics. Roughly sorted by "how sensitive is what they
 * receive."
 */
const PRODUCTION_SUBPROCESSORS: Subprocessor[] = [
  {
    name: 'Supabase',
    role: 'Managed PostgreSQL database — primary data store',
    region: 'EU (Frankfurt, eu-central-1)',
    dataReceived:
      'Everything. Children, teachers, guardians, observations, assessments, audit logs, consents.',
    safeguard:
      'POPIA s72(1)(a) Standard Contractual Clauses with Supabase. Encrypted at rest (AES-256) and in transit (TLS 1.2+). Per-tenant schema isolation enforced inside the database.',
    homepage: 'https://supabase.com',
    privacyUrl: 'https://supabase.com/privacy',
  },
  {
    name: 'Render',
    role: 'Compute platform — hosts the Sprout API',
    region: 'EU (Frankfurt) for production',
    dataReceived:
      "API request/response payloads in transit. Logs do not contain personal information by design — observation narrative, child names, and guardian email are scrubbed from log output.",
    safeguard:
      'POPIA s72(1)(a) SCCs with Render. TLS termination, automatic HTTPS, no persistent storage of request bodies.',
    homepage: 'https://render.com',
    privacyUrl: 'https://render.com/privacy',
  },
  {
    name: 'Vercel',
    role: 'Compute platform — hosts the marketing site and the staff/guardian web app',
    region: 'Global edge network; primary region EU (fra1)',
    dataReceived:
      'Static UI assets and HTML responses. No personal information stored on Vercel — all data calls go directly from the user\'s browser to the Sprout API on Render.',
    safeguard:
      'POPIA s72(1)(a) SCCs with Vercel. TLS termination, edge caching limited to public marketing routes only.',
    homepage: 'https://vercel.com',
    privacyUrl: 'https://vercel.com/legal/privacy-policy',
  },
  {
    name: 'AWS S3',
    role: 'Object storage — photos and videos attached to observations (paid plans only)',
    region: 'Configurable; production default EU (eu-west-1)',
    dataReceived:
      'Media files uploaded by teachers and the metadata required to retrieve them. Files are referenced from observations by signed URL with a 1-hour expiry; they are not publicly addressable.',
    safeguard:
      'POPIA s72(1)(a) SCCs with AWS. Server-side encryption AES-256, signed URL TTL of 1 hour, no public bucket policies.',
    homepage: 'https://aws.amazon.com/s3/',
    privacyUrl: 'https://aws.amazon.com/privacy/',
  },
  {
    name: 'Resend',
    role: 'Transactional email — sends notifications to guardians when an observation or assessment is published, plus password reset and trial expiry emails',
    region:
      'Region depends on configured Resend account. Production should be configured to Resend EU; if a US Resend account is used, additional s72 SCCs apply.',
    dataReceived:
      'Recipient name, recipient email, child name, teacher name, and a 100-character preview of the observation narrative. The full narrative and all attached media stay within Sprout — Resend never receives photos.',
    safeguard:
      'POPIA s72(1)(a) SCCs with Resend. Bearer-token API authentication. Recommended: use Resend EU region for production deployments.',
    homepage: 'https://resend.com',
    privacyUrl: 'https://resend.com/legal/privacy-policy',
  },
  {
    name: 'PostHog',
    role: 'Product analytics — page views, feature usage events, and identified user/guardian sessions for retention and conversion analysis',
    region: 'EU (eu.posthog.com)',
    dataReceived:
      'User and guardian email addresses (used as identifiers), organisation ID, role, and event payloads describing UI interactions. No observation narrative, no child names, no media.',
    safeguard:
      'EU-only host. Captures are restricted at the SDK layer — narrative text and child names are never sent. Schools can opt out organisation-wide on request to support@.',
    homepage: 'https://posthog.com',
    privacyUrl: 'https://posthog.com/privacy',
  },
];

const NON_SUBPROCESSORS = [
  {
    name: 'Ollama (local LLM)',
    why: 'Runs as a process on the same Render compute instance as the Sprout API. Observation text passed to the model never crosses an API boundary or reaches a third party. Not a subprocessor under POPIA.',
  },
  {
    name: 'MinIO',
    why: 'Used as a local S3-compatible server during development only. Production media goes to AWS S3 (listed above). Not present in production deployments.',
  },
];

const Subprocessors: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Subprocessors — Sprout</title>
        <meta
          name="description"
          content="The third-party services that process personal information on behalf of Sprout, in support of POPIA's openness condition (s17). Lists each subprocessor by role, region, data received, and safeguard mechanism."
        />
        <link rel="canonical" href="https://sprout-ui-pi.vercel.app/legal/subprocessors" />
      </Helmet>

      <article className="prose prose-sm dark:prose-invert max-w-4xl mx-auto px-4 py-12">
        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
          Last reviewed 2026-04-29
        </p>
        <h1>Subprocessors</h1>
        <p className="lead">
          POPIA's openness condition (section 17) requires us to disclose the
          third parties that process personal information on our behalf. This
          page lists every production subprocessor we use, what data they
          receive, where the data is processed, and what safeguard mechanism
          applies. We update it whenever we add, remove, or change a
          subprocessor.
        </p>

        <h2>Production subprocessors</h2>
        <div className="not-prose mt-6 overflow-x-auto">
          <table className="min-w-full text-sm border border-gray-200 dark:border-gray-700 rounded-lg">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-white">
                  Subprocessor
                </th>
                <th className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-white">
                  Role
                </th>
                <th className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-white">
                  Region
                </th>
              </tr>
            </thead>
            <tbody>
              {PRODUCTION_SUBPROCESSORS.map(s => (
                <tr key={s.name} className="border-t border-gray-200 dark:border-gray-700 align-top">
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                    <a
                      href={s.homepage}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      {s.name}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{s.role}</td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {s.region}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2>Detail: what each one receives and why</h2>
        {PRODUCTION_SUBPROCESSORS.map(s => (
          <section key={s.name} id={s.name.toLowerCase().replace(/\s+/g, '-')}>
            <h3>{s.name}</h3>
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
              {s.role}
            </p>
            <ul>
              <li>
                <strong>Region:</strong> {s.region}
              </li>
              <li>
                <strong>Data received:</strong> {s.dataReceived}
              </li>
              <li>
                <strong>Safeguard:</strong> {s.safeguard}
              </li>
              {s.privacyUrl && (
                <li>
                  <strong>Provider's privacy notice:</strong>{' '}
                  <a href={s.privacyUrl} target="_blank" rel="noreferrer">
                    {s.privacyUrl}
                  </a>
                </li>
              )}
            </ul>
          </section>
        ))}

        <h2>What is not a subprocessor</h2>
        <p>
          A few components that look like third parties from the outside are
          not actually subprocessors under POPIA, because no personal
          information leaves Sprout's own infrastructure to reach them:
        </p>
        <ul>
          {NON_SUBPROCESSORS.map(item => (
            <li key={item.name}>
              <strong>{item.name}.</strong> {item.why}
            </li>
          ))}
        </ul>

        <h2>How we change this list</h2>
        <p>
          When we add or remove a subprocessor, or change a subprocessor's
          region or scope of data received, we update this page and notify
          organisation administrators by email at least 14 days before the
          change takes effect (subject to commercially reasonable variation
          for security-driven changes). Schools can object to a new
          subprocessor; if we cannot accommodate the objection, the school
          may terminate without penalty under the{' '}
          <Link to="/legal/terms">Terms of Service</Link>.
        </p>

        <p>
          Related: <Link to="/docs/security/popia">POPIA Compliance</Link>{' '}
          covers the broader privacy framework, and{' '}
          <Link to="/docs/security/data-residency">Data Residency</Link>{' '}
          explains the production hosting layout.
        </p>
      </article>
    </>
  );
};

export default Subprocessors;
